// Frolik's description format: plain text plus a tiny, safe set of markers.
//
//   **bold**   *italic*   [link text](https://…)   ![](https://res.cloudinary.com/…)
//   A whole line wrapped as  -> text <-  is centred.
//   Every line break, blank line and space is kept exactly as typed.
//   A backslash makes the next character literal (\* stays an asterisk).
//
// It's stored as text and rendered to React elements (never raw HTML), and
// only http(s)/mailto links and images uploaded to our Cloudinary are shown.

const LINK_PATTERN = /^(https?:\/\/|mailto:)[^\s()]+$/i;
const IMAGE_PATTERN = /^https:\/\/res\.cloudinary\.com\/[^\s()]+$/i;

export const isSafeLink = (url) => LINK_PATTERN.test(String(url || '').trim());
export const isSafeImage = (url) => IMAGE_PATTERN.test(String(url || '').trim());

/* ------------------------------ Writing ---------------------------------- */

// Escapes characters that would otherwise be read as formatting.
export function escapeRichText(text) {
  return String(text || '').replace(/([\\*[\]()!])/g, '\\$1');
}

const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'H1', 'H2', 'H3', 'BLOCKQUOTE']);

function isBold(element) {
  return ['B', 'STRONG'].includes(element.tagName) || Number(element.style?.fontWeight) >= 600 || element.style?.fontWeight === 'bold';
}

function isItalic(element) {
  return ['I', 'EM'].includes(element.tagName) || element.style?.fontStyle === 'italic';
}

function isCentred(element) {
  return element.style?.textAlign === 'center' || element.getAttribute?.('align') === 'center';
}

// Turns the contents of the description editor (a contentEditable element)
// into the description format.
export function serializeEditor(root) {
  const lines = [];
  let line = { text: '', centre: false };
  const finishLine = () => {
    lines.push(line);
    line = { text: '', centre: false };
  };

  const walk = (node, centre) => {
    if (node.nodeType === 3) {
      line.text += escapeRichText(node.data.replace(/ /g, ' '));
      if (centre) line.centre = true;
      return;
    }
    if (node.nodeType !== 1) return;

    const element = node;
    if (element.tagName === 'BR') {
      finishLine();
      return;
    }
    if (element.tagName === 'IMG') {
      if (isSafeImage(element.getAttribute('src'))) {
        line.text += `![](${element.getAttribute('src')})`;
        if (centre) line.centre = true;
      }
      return;
    }

    const block = BLOCK_TAGS.has(element.tagName);
    const blockCentre = centre || isCentred(element);

    if (block && (line.text !== '' || line.centre)) finishLine();

    let open = '';
    let close = '';
    if (!block && isBold(element)) { open += '**'; close = `**${close}`; }
    if (!block && isItalic(element)) { open += '*'; close = `*${close}`; }

    if (element.tagName === 'A' && isSafeLink(element.getAttribute('href'))) {
      line.text += `${open}[`;
      element.childNodes.forEach((child) => walk(child, blockCentre));
      line.text += `](${element.getAttribute('href')})${close}`;
    } else {
      line.text += open;
      const children = [...element.childNodes];
      // A block ending in <br> (an empty line in the editor) shouldn't add another.
      if (block && children.length && children[children.length - 1].tagName === 'BR') children.pop();
      children.forEach((child) => walk(child, blockCentre));
      line.text += close;
    }

    if (block) {
      if (blockCentre) line.centre = true;
      finishLine();
    }
  };

  root.childNodes.forEach((child) => walk(child, false));
  if (line.text !== '' || line.centre || lines.length === 0) finishLine();

  // The editor keeps a trailing empty line after the last block; drop it.
  while (lines.length > 1 && lines[lines.length - 1].text === '' && !lines[lines.length - 1].centre) lines.pop();

  return lines
    .map((item) => (item.centre && item.text !== '' ? `-> ${item.text} <-` : item.text))
    .join('\n');
}

/* ------------------------------ Reading ---------------------------------- */

// Finds the next unescaped occurrence of `token` at or after `from`.
function findUnescaped(text, token, from) {
  for (let index = from; index <= text.length - token.length; index += 1) {
    if (text[index] === '\\') { index += 1; continue; }
    if (text.startsWith(token, index)) return index;
  }
  return -1;
}

// Inline parser → nodes: {type:'text'|'bold'|'italic'|'link'|'image', ...}
export function parseInline(text) {
  const nodes = [];
  let buffer = '';
  const flush = () => {
    if (buffer) nodes.push({ type: 'text', text: buffer });
    buffer = '';
  };

  let index = 0;
  while (index < text.length) {
    const char = text[index];

    if (char === '\\' && index + 1 < text.length) {
      buffer += text[index + 1];
      index += 2;
      continue;
    }

    if (text.startsWith('![](', index)) {
      const end = findUnescaped(text, ')', index + 4);
      const url = end > -1 ? text.slice(index + 4, end) : '';
      if (end > -1 && isSafeImage(url)) {
        flush();
        nodes.push({ type: 'image', url });
        index = end + 1;
        continue;
      }
    }

    if (char === '[') {
      const close = findUnescaped(text, '](', index + 1);
      const end = close > -1 ? findUnescaped(text, ')', close + 2) : -1;
      const url = end > -1 ? text.slice(close + 2, end) : '';
      if (end > -1 && isSafeLink(url)) {
        flush();
        nodes.push({ type: 'link', url, children: parseInline(text.slice(index + 1, close)) });
        index = end + 1;
        continue;
      }
    }

    if (text.startsWith('**', index)) {
      const end = findUnescaped(text, '**', index + 2);
      if (end > index + 2) {
        flush();
        nodes.push({ type: 'bold', children: parseInline(text.slice(index + 2, end)) });
        index = end + 2;
        continue;
      }
    }

    if (char === '*' && text[index + 1] !== '*') {
      const end = findUnescaped(text, '*', index + 1);
      if (end > index + 1) {
        flush();
        nodes.push({ type: 'italic', children: parseInline(text.slice(index + 1, end)) });
        index = end + 1;
        continue;
      }
    }

    buffer += char;
    index += 1;
  }

  flush();
  return nodes;
}

const CENTRE_PATTERN = /^-> ([\s\S]*) <-$/;

// Whole description → lines: [{ centre, nodes }]. Every line is kept.
export function parseRichText(text) {
  return String(text || '').split('\n').map((raw) => {
    const match = raw.match(CENTRE_PATTERN);
    return { centre: Boolean(match), nodes: parseInline(match ? match[1] : raw) };
  });
}

function nodesToPlain(nodes) {
  return nodes.map((node) => {
    if (node.type === 'text') return node.text;
    if (node.type === 'image') return '';
    return nodesToPlain(node.children || []);
  }).join('');
}

// Readable plain text (for the share card image and other plain-text places).
export function richTextToPlain(text) {
  return parseRichText(text).map((line) => nodesToPlain(line.nodes)).join('\n').trim();
}


/* ------------------------------ HTML view -------------------------------- */
// The editor's "HTML" button shows the description as HTML (like Blogspot's
// HTML view) and reads edited HTML back. Going to HTML builds markup only
// from escaped text; coming back parses it inertly (nothing runs, no images
// load) and keeps just what the description format allows: bold, italic,
// centred lines, safe links and Frolik-hosted images. Anything else is dropped.

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

function nodesToHtml(nodes) {
  return nodes.map((node) => {
    switch (node.type) {
      case 'bold': return `<b>${nodesToHtml(node.children)}</b>`;
      case 'italic': return `<i>${nodesToHtml(node.children)}</i>`;
      case 'link': return `<a href="${escapeHtml(node.url)}">${nodesToHtml(node.children)}</a>`;
      case 'image': return `<img src="${escapeHtml(node.url)}">`;
      default: return escapeHtml(node.text);
    }
  }).join('');
}

// Description → HTML, one <div> per line.
export function richTextToHtml(text) {
  if (!String(text || '')) return '';
  return parseRichText(text)
    .map((line) => {
      const inner = nodesToHtml(line.nodes) || '<br>';
      return line.centre ? `<div style="text-align: center;">${inner}</div>` : `<div>${inner}</div>`;
    })
    .join('\n');
}

// HTML (as typed in the HTML view) → description.
export function htmlToRichText(html) {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${String(html || '')}</body>`, 'text/html');
  // Scripts, styles and embeds go entirely, contents included.
  doc.body.querySelectorAll('script, style, template, noscript, iframe, object, embed, svg, math').forEach((element) => element.remove());
  // Line breaks between blocks are just layout in the HTML view.
  [...doc.body.childNodes].forEach((node) => {
    if (node.nodeType === 3 && !node.data.trim() && node.data.includes('\n')) node.remove();
  });
  return serializeEditor(doc.body);
}
